<template>
  <div 
    class="tabs-container" 
    ref="tabsContainer" 
    :style="{ width: containerWidthProp }"
    :class="[isRightMax ? 'right-max' : '', isLeftMax ? 'left-max' : '']"
  >
    <div 
      class="tabs-scroll-wrapper" 
      ref="scrollWrapper"
      @scroll="handleScroll"
    >
      <div 
        class="tabs-list" 
        ref="tabsList"
      >
        <div
          v-for="(tab, index) in tabs"
          :key="tab.key || index"
          class="tab-item"
          :class="{ active: activeKey === (tab.key || tab.name || tab.categoryName || index) }"
          @click="handleTabClick(tab, index)"
          ref="tabItems"
        >
          <img v-if="index === 0" :src=" activeKey === (tab.key || tab.name || tab.categoryName || index) ? hotIconActive : hotIcon" alt="icon" class="hot-icon" />
          <span class="tab-text">{{ tab.label || tab.name || tab.categoryName }}</span>
        </div>
        <!-- 右侧间距占位元素 -->
        <div class="tabs-list-padding" aria-hidden="true"></div>
      </div>
    </div>
  </div>
</template>

<script>
import hotIcon from '@/assets/image/hot_icon.png'
import hotIconActive from '@/assets/image/hot_icon_active.png'

export default {
  name: 'ForceScrollTabs',
  props: {
    tabs: {
      type: Array,
      required: true,
      default: () => []
    },
    value: {
      type: [String, Number],
      default: 0
    },
    containerWidthProp: {
      type: [String, Number],
      default: '100%'
    },
    tabMinWidthProp: {
      type: [String, Number],
      default: '120px'
    }
  },
  data() {
    return {
      activeKey: this.value,
      tabWidth: 0, // 单个Tab的实际宽度（缓存）
      isRightMax: false,
      isLeftMax: false,
      hotIcon,
      hotIconActive
    }
  },
  watch: {
    value(newVal) {
      this.activeKey = newVal
    }
  },
  mounted() {
    this.$nextTick(() => {
      // 初始化获取单个Tab宽度
      if (this.$refs.tabItems && this.$refs.tabItems.length) {
        this.tabWidth = this.$refs.tabItems[0].offsetWidth
        console.log('初始化Tab宽度：', this.tabWidth)
      }
      const activeIndex = this.getActiveIndex(this.activeKey)
      this.forceScrollToExposeNextTab(activeIndex)
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    getActiveIndex(key) {
      return this.tabs.findIndex(tab => (tab.key || tab.name || this.tabs.indexOf(tab)) === key)
    },

    handleTabClick(tab, index) {
      const key = tab.key || tab.name || tab.categoryName || index
      // 如果点击的是已选中的tab，不执行任何操作
      if (this.activeKey === key) return
      
      this.activeKey = key
      this.$emit('input', key)
      this.$emit('change', tab, index)
      
      // 强制滚动（增加延迟确保DOM完全更新）
      setTimeout(() => {
        this.forceScrollToExposeNextTab(index)
      }, 50)
    },

    // 核心修改：直接计算目标滚动位置，确保右侧留一个Tab宽度
    forceScrollToExposeNextTab(activeIndex) {
      const scrollWrapper = this.$refs.scrollWrapper
      const tabList = this.$refs.tabsList
      const tabItems = this.$refs.tabItems

      if (!scrollWrapper || !tabList || !tabItems || tabItems.length === 0) return

      // 1. 基础尺寸
      const containerWidth = scrollWrapper.clientWidth
      const listWidth = tabList.scrollWidth
      const maxScroll = listWidth - containerWidth // 最大可滚动距离

      if (maxScroll <= 0) {
        this.updateScrollState(0, maxScroll)
        return
      }

      // 2. 计算激活Tab的累计偏移（从列表左侧到激活Tab右侧的距离）
      // gap 为 12px，需要加上
      const gap = 12
      let activeTabRightOffset = 0
      for (let i = 0; i <= activeIndex; i++) {
        activeTabRightOffset += tabItems[i].offsetWidth
        if (i < activeIndex) {
          activeTabRightOffset += gap // 每两个 tab 之间加一个 gap
        }
      }
      // 预留一个Tab宽度（确保后续项露出）
      const targetRightOffset = activeTabRightOffset + this.tabWidth

      // 3. 计算目标滚动偏移（核心：让激活Tab右侧预留一个Tab宽度在容器内）
      let targetOffset = 0
      if (targetRightOffset > containerWidth) {
        // 需要滚动的距离 = 目标右侧偏移 - 容器宽度
        targetOffset = targetRightOffset - containerWidth
        // 边界限制
        targetOffset = Math.min(maxScroll, targetOffset)
      }

      // 4. 应用滚动
      scrollWrapper.scrollTo({
        left: targetOffset,
        behavior: 'smooth'
      })

      this.updateScrollState(targetOffset, maxScroll)
    },
    
    // 处理手动滚动
    handleScroll() {
      const scrollWrapper = this.$refs.scrollWrapper
      if (!scrollWrapper) return
      
      const scrollLeft = scrollWrapper.scrollLeft
      const containerWidth = scrollWrapper.clientWidth
      const listWidth = this.$refs.tabsList.scrollWidth
      const maxScroll = listWidth - containerWidth
      
      this.updateScrollState(scrollLeft, maxScroll)
    },
    
    // 更新滚动状态
    updateScrollState(scrollLeft, maxScroll) {
      if (scrollLeft >= maxScroll - 1) { // 1px容错
        this.isRightMax = true
      } else {
        this.isRightMax = false
      }
      
      if (scrollLeft <= 1) { // 1px容错
        this.isLeftMax = true
      } else {
        this.isLeftMax = false
      }
    },

    handleResize() {
      this.$nextTick(() => {
        const activeIndex = this.getActiveIndex(this.activeKey)
        this.forceScrollToExposeNextTab(activeIndex)
      })
    }
  }
}
</script>

<style scoped lang="less">
.tabs-container {
  position: relative;
  overflow: hidden;
  user-select: none;
  line-height: 1.5;
  width: 100%;
  box-sizing: border-box;
  .px2rem(margin-bottom, 6);
}

.tabs-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  /* 移除可能的内边距干扰 */
  padding: 0 !important;
  margin: 0 !important;
  /* 隐藏滚动条 */
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.tabs-scroll-wrapper::-webkit-scrollbar {
  display: none;
}

.tabs-list {
  display: flex;
  flex-wrap: nowrap !important;
  will-change: transform;
  padding: 0 !important;
  margin: 0 !important;
  .px2rem(gap, 12);
}

.tabs-list-padding {
  flex-shrink: 0;
  .px2rem(width, 1);
  .px2rem(height, 30);
}

.tab-item {
  min-width: inherit;
  width: auto;
  .px2rem(height, 30);
  .px2rem(line-height, 30);
  .px2rem(padding-left, 12);
  .px2rem(padding-right, 12);
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0 !important;
  box-sizing: border-box;
  transition: all 0.2s ease;
  margin: 0 !important;
  position: relative;
  .px2rem(font-size, 12);
  color: #5C606A;
  background-color: #F5F7FA;
  .px2rem(border-radius, 8);
  -webkit-tap-highlight-color: transparent;
  outline: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  font-family: "PingFang SC";
}

.tab-item:active {
  -webkit-tap-highlight-color: transparent;
}

.tab-item.active {
  color: #2290FD;
  background-color: rgba(34, 144, 253, 0.08);
}


/* 滚动提示渐变遮罩（可选） */
.tabs-container::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 72px;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.tabs-container::after {
  right: 0;
  background: linear-gradient(270deg, rgba(255, 254.74501037597656, 254.74501037597656, 1) 1%,rgba(255, 254.74501037597656, 254.74501037597656, 0) 99%);
}

.tabs-container.right-max::after{
  display: none;
}

/* 有左滚偏移时显示右侧遮罩 */
.tabs-container:not([style*="scrollOffset: 0"])::after {
  opacity: 1;
}

.hot-icon{
  .px2rem(width, 16);
  .px2rem(margin-right, 4);
}

</style>
